import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTheme } from '../../../hooks/useTheme';
import Card3D from '../../../components/base/Card3D';
import MagneticButton from '../../../components/base/MagneticButton';
import ParticleBackground from '../../../components/base/ParticleBackground';
import AnimatedCounter from '../../../components/base/AnimatedCounter';

export default function DeveloperSupport() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();

  return (
    <section
      id="developers"
      className={`py-16 md:py-24 lg:py-28 relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#161b22]' : 'bg-[#f8fafb]'
      }`}
    >
      {/* 背景装饰 */}
      <div
        className={`absolute top-0 left-1/4 w-[250px] md:w-[500px] h-[250px] md:h-[500px] ${
          isDark ? 'bg-[#E84A1F]/5' : 'bg-[#E84A1F]/3'
        } rounded-full blur-[120px]`}
      ></div>
      <div
        className={`absolute bottom-0 right-1/4 w-[200px] md:w-[400px] h-[200px] md:h-[400px] ${
          isDark ? 'bg-[#F06030]/5' : 'bg-[#F06030]/3'
        } rounded-full blur-[100px]`}
      ></div>

      {/* 中心大光晕 */}
      {/* NOTE: Fixed invalid Tailwind class syntax that caused the build error.
          Replaced `from-[#E84A1F]/[0.03]` / `from-[#E84A1F]/[0.02]` with valid opacity utilities. */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[1000px] h-[600px] md:h-[1000px] bg-gradient-radial ${
          isDark ? 'from-[#E84A1F]/5' : 'from-[#E84A1F]/3'
        } via-transparent to-transparent blur-[80px]`}
      ></div>

      {/* 顶部渐变 */}
      <div
        className={`absolute top-0 left-0 right-0 h-[200px] md:h-[300px] bg-gradient-to-b ${
          isDark ? 'from-[#0d1117]/50' : 'from-white/50'
        } to-transparent`}
      ></div>

      {/* 网格背景 */}
      <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.05]' : 'opacity-[0.03]'}`}
        style={{
          backgroundImage: `linear-gradient(${isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'} 1px, transparent 1px), linear-gradient(90deg, ${
            isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'
          } 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      ></div>

      {/* 代码风格点阵 */}
      <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.02]' : 'opacity-[0.015]'}`}
        style={{
          backgroundImage: 'radial-gradient(circle, #E84A1F 1.5px, transparent 1.5px)',
          backgroundSize: '24px 24px',
        }}
      ></div>

      {/* 装饰性代码块形状 */}
      <div
        className={`absolute top-[10%] right-[5%] w-[100px] md:w-[200px] h-[60px] md:h-[120px] border ${
          isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'
        } rounded-lg hidden md:block`}
      ></div>
      <div
        className={`absolute bottom-[15%] left-[3%] w-[80px] md:w-[150px] h-[80px] md:h-[150px] border border-dashed ${
          isDark ? 'border-[#F06030]/10' : 'border-[#F06030]/5'
        } rounded-xl hidden md:block`}
      ></div>

      {/* 终端风格装饰线 */}
      <div
        className={`absolute top-[20%] left-0 w-[100px] md:w-[200px] h-px bg-gradient-to-r ${
          isDark ? 'from-[#E84A1F]/30' : 'from-[#E84A1F]/20'
        } to-transparent hidden md:block`}
      ></div>
      <div
        className={`absolute top-[22%] left-0 w-[60px] md:w-[120px] h-px bg-gradient-to-r ${
          isDark ? 'from-[#F06030]/25' : 'from-[#F06030]/15'
        } to-transparent hidden md:block`}
      ></div>
      <div
        className={`absolute bottom-[30%] right-0 w-[80px] md:w-[150px] h-px bg-gradient-to-l ${
          isDark ? 'from-[#E84A1F]/30' : 'from-[#E84A1F]/20'
        } to-transparent hidden md:block`}
      ></div>

      {/* 浮动装饰 */}
      <div
        className={`absolute top-[25%] right-[15%] w-2 h-2 ${
          isDark ? 'bg-[#E84A1F]/30' : 'bg-[#E84A1F]/20'
        } rounded-full hidden lg:block`}
      ></div>
      <div
        className={`absolute top-[45%] left-[8%] w-3 h-3 ${
          isDark ? 'bg-[#F06030]/25' : 'bg-[#F06030]/15'
        } rounded-full hidden lg:block`}
      ></div>
      <div
        className={`absolute bottom-[20%] right-[10%] w-2 h-2 ${
          isDark ? 'bg-[#E84A1F]/25' : 'bg-[#E84A1F]/15'
        } rounded-full hidden lg:block`}
      ></div>

      {/* 粒子背景效果 */}
      <ParticleBackground 
        particleCount={65} 
        speed={0.4} 
        connectDistance={100}
      />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* 标题区域 */}
        <div
          ref={headerRef}
          className={`mb-10 md:mb-16 relative transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 装饰性大数字 */}
          <div
            className={`absolute -top-8 -left-4 text-[80px] md:text-[140px] font-bold ${
              isDark ? 'text-white/[0.02]' : 'text-[#0d1117]/[0.02]'
            } leading-none select-none pointer-events-none hidden md:block`}
          >
            04
          </div>

          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-12">
            {/* 左侧编号栏 */}
            <div className="flex md:flex-col items-center gap-3 pt-0 md:pt-2">
              <div className="w-9 md:w-10 h-9 md:h-10 rounded-full bg-gradient-to-br from-[#E84A1F] to-[#F06030] flex items-center justify-center text-white font-bold text-sm">
                04
              </div>
              <div
                className={`hidden md:block w-px bg-gradient-to-b from-[#E84A1F] to-transparent mt-3 transition-all duration-1000 delay-300 ${
                  headerVisible ? 'h-20' : 'h-0'
                }`}
              ></div>
            </div>

            {/* 标题内容 */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3 md:mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#E84A1F] rounded-full animate-pulse"></span>
                  <span
                    className={`text-xs uppercase tracking-widest hidden sm:inline ${
                      isDark ? 'text-gray-500' : 'text-[#0d1117]/40'
                    }`}
                  >
                    Build on RoonChain
                  </span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-start justify-between gap-4 md:gap-0">
                <h2
                  className={`text-2xl sm:text-3xl md:text-4xl font-bold transition-colors duration-500 ${
                    isDark ? 'text-white' : 'text-[#0d1117]'
                  }`}
                >
                  {t('dev.title')}
                </h2>
                <div
                  className={`max-w-full md:max-w-[400px] pl-0 md:pl-8 border-l-0 md:border-l transition-all duration-1000 delay-500 ${
                    isDark ? 'border-white/10' : 'border-[#0d1117]/10'
                  } ${headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-10'}`}
                >
                  <p
                    className={`text-xs md:text-sm leading-relaxed ${
                      isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
                    }`}
                  >
                    {t('dev.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 主内容区域 */}
        <div
          ref={cardsRef}
          className={`backdrop-blur-sm border rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 relative overflow-hidden transition-all duration-1000 shadow-xl ${
            isDark
              ? 'bg-[#0d1117]/80 border-white/5 shadow-black/20'
              : 'bg-white/80 border-[#0d1117]/5 shadow-[#0d1117]/5'
          } ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
        >
          {/* 卡片内装饰 */}
          <div
            className={`absolute top-0 right-0 w-[150px] md:w-[300px] h-[150px] md:h-[300px] ${
              isDark ? 'bg-[#E84A1F]/5' : 'bg-[#E84A1F]/3'
            } rounded-full blur-[80px]`}
          ></div>
          <div
            className={`absolute bottom-0 left-0 w-[100px] md:w-[200px] h-[100px] md:h-[200px] ${
              isDark ? 'bg-[#F06030]/5' : 'bg-[#F06030]/3'
            } rounded-full blur-[60px]`}
          ></div>

          {/* 三列卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 relative z-10 mb-6 md:mb-8">
            {/* Card 1 - EVM Compatible */}
            <div
              className={`relative group transition-all duration-700 ${
                cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              <Card3D intensity={12} scale={1.03}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-xl md:rounded-2xl p-4 md:p-5 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden ${
                    isDark ? 'bg-[#161b22] border-white/10' : 'bg-[#f8fafb] border-[#0d1117]/10'
                  }`}
                >
                  {/* 角标装饰 */}
                  <div
                    className={`absolute top-3 right-3 text-xs font-mono ${
                      isDark ? 'text-white/10' : 'text-[#0d1117]/10'
                    }`}
                  >
                    01
                  </div>

                  {/* 顶部标签 */}
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <div className="w-8 md:w-9 h-8 md:h-9 bg-gradient-to-br from-[#E84A1F] to-[#F06030] rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
                      <i className="ri-code-s-slash-line text-base md:text-lg text-white"></i>
                    </div>
                    <div>
                      <h3
                        className={`text-xs md:text-sm font-semibold ${isDark ? 'text-white' : 'text-[#0d1117]'}`}
                      >
                        {t('dev.evm.title')}
                      </h3>
                      <span className="text-[10px] text-[#E84A1F]">Solidity Ready</span>
                    </div>
                  </div>

                  {/* Code Editor Preview */}
                  <div
                    className={`rounded-lg md:rounded-xl p-2.5 md:p-3 font-mono text-xs overflow-hidden group-hover:border-[#E84A1F]/20 transition-all ${
                      isDark
                        ? 'bg-[#0d1117] border border-white/10'
                        : 'bg-[#1a1a24] border border-[#0d1117]/20'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-white/5">
                      <div className="w-2 h-2 bg-red-500/80 rounded-full"></div>
                      <div className="w-2 h-2 bg-yellow-500/80 rounded-full"></div>
                      <div className="w-2 h-2 bg-green-500/80 rounded-full"></div>
                      <span className="text-white/20 text-[10px] ml-1 hidden sm:inline">
                        RWAContract.sol
                      </span>
                    </div>
                    <div className="space-y-1 text-[10px] md:text-[11px] leading-relaxed">
                      <div>
                        <span className="text-[#E84A1F]">pragma</span>{' '}
                        <span className="text-[#F06030]">solidity</span>{' '}
                        <span className="text-[#F58050]">^0.8.0</span>;
                      </div>
                      <div className="h-1"></div>
                      <div>
                        <span className="text-[#E84A1F]">contract</span>{' '}
                        <span className="text-white">RWAAsset</span> {'{'}
                      </div>
                      <div className="pl-2">
                        <span className="text-[#F06030]">mapping</span>(
                        <span className="text-[#F06030]">address</span> =&gt;{' '}
                        <span className="text-[#F06030]">uint</span>){' '}
                        <span className="text-[#F58050]">balances</span>;
                      </div>
                      <div className="pl-2 hidden sm:block">
                        <span className="text-[#E84A1F]">function</span>{' '}
                        <span className="text-white">transfer</span>() {'{'}
                      </div>
                      <div className="pl-4 text-white/30 hidden sm:block">
                        // Transfer logic
                      </div>
                      <div className="pl-2 hidden sm:block">{'}'}</div>
                      <div>{'}'}</div>
                    </div>
                  </div>

                  <p
                    className={`text-xs mt-3 md:mt-4 leading-relaxed ${
                      isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
                    }`}
                  >
                    {t('dev.evm.desc')}
                  </p>

                  {/* 底部标签 */}
                  <div className="flex flex-wrap gap-1.5 mt-2 md:mt-3">
                    {['Solidity', 'Vyper', 'Web3.js'].map((tag, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-[#E84A1F]/10 border border-[#E84A1F]/20 rounded text-[10px] text-[#E84A1F]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Card3D>
            </div>

            {/* Card 2 - Developer Tools */}
            <div
              className={`relative group transition-all duration-700 ${
                cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <Card3D intensity={12} scale={1.03}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#F06030]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-xl md:rounded-2xl p-4 md:p-5 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden ${
                    isDark ? 'bg-[#161b22] border-white/10' : 'bg-[#f8fafb] border-[#0d1117]/10'
                  }`}
                >
                  {/* 角标装饰 */}
                  <div
                    className={`absolute top-3 right-3 text-xs font-mono ${
                      isDark ? 'text-white/10' : 'text-[#0d1117]/10'
                    }`}
                  >
                    02
                  </div>

                  {/* 顶部标签 */}
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <div className="w-8 md:w-9 h-8 md:h-9 bg-gradient-to-br from-[#F06030] to-[#E84A1F] rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-[#F06030]/20">
                      <i className="ri-tools-line text-base md:text-lg text-white"></i>
                    </div>
                    <div>
                      <h3
                        className={`text-xs md:text-sm font-semibold ${isDark ? 'text-white' : 'text-[#0d1117]'}`}
                      >
                        {t('dev.tools.title')}
                      </h3>
                      <span className="text-[10px] text-[#F06030]">Full Stack</span>
                    </div>
                  </div>

                  {/* Tools Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { icon: 'ri-terminal-box-line', name: t('dev.tools.rpc'), color: '#E84A1F' },
                      { icon: 'ri-search-line', name: t('dev.tools.explorer'), color: '#F06030' },
                      { icon: 'ri-code-s-slash-line', name: t('dev.tools.api'), color: '#F58050' },
                      { icon: 'ri-book-open-line', name: t('dev.tools.sdk'), color: '#E84A1F' },
                    ].map((tool, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg md:rounded-xl p-2.5 md:p-3 flex flex-col items-center justify-center hover:border-[#E84A1F]/20 transition-all cursor-pointer group/tool ${
                          isDark ? 'bg-[#0d1117] border-white/10' : 'bg-white border-[#0d1117]/10'
                        }`}
                      >
                        <div
                          className="w-8 md:w-10 h-8 md:h-10 rounded-lg flex items-center justify-center mb-1.5 md:mb-2 transition-transform group-hover/tool:scale-110"
                          style={{ backgroundColor: `${tool.color}15`, border: `1px solid ${tool.color}30` }}
                        >
                          <i className={`${tool.icon} text-lg md:text-xl`} style={{ color: tool.color }}></i>
                        </div>
                        <span
                          className={`text-[10px] md:text-xs font-medium text-center ${
                            isDark ? 'text-white' : 'text-[#0d1117]'
                          }`}
                        >
                          {tool.name}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p
                    className={`text-xs mt-3 md:mt-4 leading-relaxed ${
                      isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
                    }`}
                  >
                    {t('dev.tools.desc')}
                  </p>
                </div>
              </Card3D>
            </div>

            {/* Card 3 - Documentation */}
            <div
              className={`relative group transition-all duration-700 md:col-span-2 lg:col-span-1 ${
                cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '300ms' }}
            >
              <Card3D intensity={12} scale={1.03}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#C83A15]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-xl md:rounded-2xl p-4 md:p-5 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden ${
                    isDark ? 'bg-[#161b22] border-white/10' : 'bg-[#f8fafb] border-[#0d1117]/10'
                  }`}
                >
                  {/* 角标装饰 */}
                  <div
                    className={`absolute top-3 right-3 text-xs font-mono ${
                      isDark ? 'text-white/10' : 'text-[#0d1117]/10'
                    }`}
                  >
                    03
                  </div>

                  {/* 顶部标签 */}
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <div className="w-8 md:w-9 h-8 md:h-9 bg-gradient-to-br from-[#C83A15] to-[#E84A1F] rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-[#C83A15]/20">
                      <i className="ri-file-list-3-line text-base md:text-lg text-white"></i>
                    </div>
                    <div>
                      <h3
                        className={`text-xs md:text-sm font-semibold ${isDark ? 'text-white' : 'text-[#0d1117]'}`}
                      >
                        {t('dev.docs.title')}
                      </h3>
                      <span className="text-[10px] text-[#F58050]">Comprehensive</span>
                    </div>
                  </div>

                  {/* Documentation Preview */}
                  <div className="space-y-1.5 md:space-y-2">
                    {[
                      {
                        icon: 'ri-rocket-line',
                        title: t('dev.docs.quickstart'),
                        desc: t('dev.docs.quickstart.desc'),
                        color: '#E84A1F',
                      },
                      {
                        icon: 'ri-book-2-line',
                        title: t('dev.docs.api'),
                        desc: t('dev.docs.api.desc'),
                        color: '#F06030',
                      },
                      {
                        icon: 'ri-code-box-line',
                        title: t('dev.docs.examples'),
                        desc: t('dev.docs.examples.desc'),
                        color: '#F58050',
                      },
                      {
                        icon: 'ri-lightbulb-line',
                        title: t('dev.docs.best'),
                        desc: t('dev.docs.best.desc'),
                        color: '#E84A1F',
                      },
                    ].map((doc, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-2.5 md:gap-3 p-2 md:p-2.5 border rounded-lg md:rounded-xl hover:border-[#E84A1F]/20 transition-all cursor-pointer group/doc ${
                          isDark ? 'bg-[#0d1117] border-white/10' : 'bg-white border-[#0d1117]/10'
                        }`}
                      >
                        <div
                          className="w-7 md:w-8 h-7 md:h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover/doc:scale-110"
                          style={{ backgroundColor: `${doc.color}15`, border: `1px solid ${doc.color}30` }}
                        >
                          <i className={`${doc.icon} text-xs md:text-sm`} style={{ color: doc.color }}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className={`text-[10px] md:text-xs font-medium truncate ${
                              isDark ? 'text-white' : 'text-[#0d1117]'
                            }`}
                          >
                            {doc.title}
                          </div>
                          <div
                            className={`text-[10px] truncate hidden sm:block ${
                              isDark ? 'text-gray-500' : 'text-[#0d1117]/50'
                            }`}
                          >
                            {doc.desc}
                          </div>
                        </div>
                        <i
                          className={`ri-arrow-right-up-line text-xs md:text-sm opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 hidden sm:inline`}
                        ></i>
                      </div>
                    ))}
                  </div>

                  <p
                    className={`text-xs mt-3 md:mt-4 leading-relaxed hidden sm:block ${
                      isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
                    }`}
                  >
                    {t('dev.docs.desc')}
                  </p>
                </div>
              </Card3D>
            </div>
          </div>

          {/* 底部 CTA 区域 */}
          <div
            className={`relative z-10 bg-gradient-to-r from-[#E84A1F]/10 via-[#F06030]/5 to-transparent rounded-xl md:rounded-2xl p-4 md:p-6 border border-[#E84A1F]/10 transition-all duration-700 delay-400 ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4 md:gap-6">
                <div className="flex flex-row md:flex-col items-center gap-2">
                  <span className="text-xs font-medium text-[#E84A1F] tracking-wider hidden md:block">
                    SECTION
                  </span>
                  <span className="text-2xl md:text-4xl font-bold text-[#E84A1F]">04</span>
                  <div
                    className={`hidden md:block w-px bg-gradient-to-b from-[#E84A1F] to-transparent transition-all duration-1000 delay-300 ${
                      headerVisible ? 'h-8' : 'h-0'
                    }`}
                  ></div>
                </div>
                <div>
                  <h3
                    className={`text-base md:text-lg font-semibold mb-0.5 md:mb-1 ${
                      isDark ? 'text-white' : 'text-[#0d1117]'
                    }`}
                  >
                    {t('dev.cta.title')}
                  </h3>
                  <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>
                    {t('dev.cta.desc')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <MagneticButton
                  intensity={0.35}
                  scale={1.06}
                  className={`group relative flex-1 sm:flex-none px-4 md:px-6 py-2.5 md:py-3 border rounded-lg md:rounded-xl text-xs md:text-sm font-semibold overflow-hidden transition-colors duration-300 hover:border-[#E84A1F]/30 active:scale-95 whitespace-nowrap ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                      : 'bg-[#0d1117]/5 border-[#0d1117]/10 text-[#0d1117] hover:bg-[#0d1117]/10'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#E84A1F]/0 via-[#E84A1F]/10 to-[#E84A1F]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#E84A1F] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <i className="ri-github-line text-base md:text-lg group-hover:rotate-[360deg] transition-transform duration-700"></i>
                    {t('dev.cta.github')}
                    <i className="ri-arrow-right-up-line text-xs md:text-sm opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 hidden sm:inline"></i>
                  </span>
                </MagneticButton>
              </div>
            </div>
          </div>
        </div>

        {/* 底部统计指标 */}
        <div ref={statsRef} className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { value: 500, labelKey: 'dev.stats.developers', icon: 'ri-user-star-line', suffix: '+' },
            { value: 1200, labelKey: 'dev.stats.contracts', icon: 'ri-file-code-line', suffix: '+' },
            { value: 50, labelKey: 'dev.stats.projects', icon: 'ri-git-repository-line', suffix: '+' },
            { value: 24, labelKey: 'dev.stats.support', icon: 'ri-customer-service-2-line', suffix: '/7', isSpecial: true },
          ].map((stat, index) => (
            <Card3D key={index} intensity={15} scale={1.04}>
              <div
                className={`border rounded-xl md:rounded-2xl p-3 md:p-4 text-center relative group hover:border-[#E84A1F]/20 transition-all duration-500 cursor-pointer shadow-lg ${
                  isDark
                    ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                } ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-[#E84A1F]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl md:rounded-2xl"></div>

                <div className="relative z-10">
                  <div className="w-9 md:w-10 h-9 md:h-10 bg-gradient-to-br from-[#E84A1F]/10 to-[#F06030]/10 border border-[#E84A1F]/20 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-1.5 md:mb-2 group-hover:scale-110 transition-transform">
                    <i className={`${stat.icon} text-base md:text-lg text-[#E84A1F]`}></i>
                  </div>
                  <div className={`text-lg md:text-xl font-bold mb-0.5 group-hover:text-[#E84A1F] transition-colors ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                    {stat.isSpecial ? (
                      <><AnimatedCounter end={stat.value} duration={1500} />{stat.suffix}</>
                    ) : (
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} duration={2000} />
                    )}
                  </div>
                  <div className={`text-[10px] md:text-xs ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t(stat.labelKey)}</div>
                </div>

                <div className={`absolute top-2 right-2 text-[10px] font-mono ${isDark ? 'text-white/10' : 'text-[#0d1117]/10'}`}>0{index + 1}</div>
              </div>
            </Card3D>
          ))}
        </div>

        {/* 底部连接线装饰 */}
        <div className={`hidden md:flex items-center justify-center mt-8 gap-2`}>
          <div className={`w-20 h-px bg-gradient-to-r from-transparent ${isDark ? 'to-white/10' : 'to-[#0d1117]/10'}`}></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/30"></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/50"></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/30"></div>
          <div className={`w-20 h-px bg-gradient-to-l from-transparent ${isDark ? 'to-white/10' : 'to-[#0d1117]/10'}`}></div>
        </div>
      </div>
    </section>
  );
}
